import { SUBCLASSES_BY_CLASS, BACKGROUND_DATA, LINEAGE_OPTIONS, FIGHTING_STYLE_OPTIONS, METAMAGIC_OPTIONS, INVOCATION_OPTIONS, TOOL_OPTIONS, INSTRUMENT_OPTIONS } from '../data/constants';
import { FEATS } from '../data/feats';
import { RACE_FEATURES } from '../data/raceFeatures';
import { SPELLS } from '../data/spells';
import './Traits.css';

const Traits = ({ character, updateCharacter, updateFeatureChoice, computedFeats }) => {
    const { class: charClass, subclass, race, lineage, background, features, featureChoices = {}, proficiencies = {} } = character;
    const knownLanguages = proficiencies.languages || [];
    const knownTools = proficiencies.tools || [];
    const knownSkills = proficiencies.skills || [];
    const bgFeatName = BACKGROUND_DATA[background]?.feat;

    // Sort features by level (Ascending)
    const sortedFeatures = [...(features || [])].sort((a, b) => a.level - b.level);

    const classFeatures = sortedFeatures.filter(f => f.source === 'Class');
    const subclassFeatures = sortedFeatures.filter(f => f.source === 'Subclass');
    // Exclude Feats as they have their own tab. Exclude Class/Subclass/Race as they are handled above.
    const genericFeatures = sortedFeatures.filter(f => f.source !== 'Feat' && f.source !== 'Class' && f.source !== 'Subclass' && f.source !== 'Race');
    const raceFeatures = RACE_FEATURES[race] || [];
    const lineageFeatures = lineage ? (RACE_FEATURES[lineage] || []) : [];

    // console.log('Traits Debug:', { race, lineage, raceFeatures, lineageFeatures });

    const handleSubclassChange = (e) => {
        updateCharacter(['subclass'], e.target.value);
    };

    const handleLineageChange = (e) => {
        updateCharacter(['lineage'], e.target.value);
    };

    const renderFeature = (feat, idx) => {
        const isInteractiveSubclass = feat.interactive === 'subclass';
        const isInteractiveASI = feat.interactive === 'asi';
        const isInteractiveLanguage = feat.interactive === 'language';
        const isInteractiveCantrip = feat.interactive === 'cantrip';
        const isInteractiveLineage = feat.interactive === 'lineage';
        const isInteractiveFightingStyle = feat.interactive === 'fightingStyle';
        const isInteractiveMetamagic = feat.interactive === 'metamagic';
        const isInteractiveInvocation = feat.interactive === 'invocation';
        const isInteractiveOriginFeat = feat.interactive === 'originFeat';
        const availableSubclasses = SUBCLASSES_BY_CLASS[charClass] || [];
        const availableLineages = LINEAGE_OPTIONS[race] || [];

        const uniqueKey = `${feat.level}-${feat.name}`;
        const currentChoice = featureChoices[uniqueKey] || {}; // { type: 'asi'|'feat', value: ... }

        // Logic for interactive types
        const handleInteractionChange = (value) => {
            updateFeatureChoice(uniqueKey, feat.interactive, value);
        };

        // Helper to check if option is already taken
        const isTaken = (val, list) => {
            if (val === currentChoice.value) return false;
            return list.includes(val);
        };

        const languages = ['Elvish', 'Dwarvish', 'Giant', 'Gnomish', 'Goblin', 'Halfling', 'Orc', 'Abyssal', 'Celestial', 'Draconic', 'Deep Speech', 'Infernal', 'Primordial', 'Sylvan', 'Undercommon'];
        const wizardCantrips = SPELLS.filter(s => s.level === 0 && s.classes.includes('Wizard')).map(s => s.name);

        return (
            <div key={`${feat.name}-${idx}`} className="feature-card">
                <div className="feature-header">
                    <h3>{feat.name}</h3>
                    <span className="feature-level-badge">Level {feat.level}</span>
                </div>
                <div className="feature-meta">
                    {feat.type && <span className="feature-type">{feat.type}</span>}
                    {feat.cost && <span className="feature-type cost">{feat.cost}</span>}
                </div>
                <p className="feature-desc">{feat.desc}</p>

                {isInteractiveSubclass && (
                    <div className="feature-interaction">
                        <select
                            value={subclass || ''}
                            onChange={handleSubclassChange}
                            className="trait-dropdown"
                        >
                            <option value="" disabled>Select {feat.name}...</option>
                            {availableSubclasses.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>
                )}

                {isInteractiveLineage && (
                    <div className="feature-interaction">
                        <select
                            value={lineage || ''}
                            onChange={handleLineageChange}
                            className="trait-dropdown"
                        >
                            <option value="" disabled>Select Lineage...</option>
                            {availableLineages.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>
                )}

                {isInteractiveLanguage && (
                    <div className="feature-interaction">
                        <select
                            value={currentChoice.value || ''}
                            onChange={(e) => handleInteractionChange(e.target.value)}
                            className="trait-dropdown"
                        >
                            <option value="" disabled>Select Language...</option>
                            {languages.map(opt => (
                                <option key={opt} value={opt} disabled={isTaken(opt, knownLanguages)}>
                                    {opt} {isTaken(opt, knownLanguages) ? '(Taken)' : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {isInteractiveCantrip && (
                    <div className="feature-interaction">
                        <select
                            value={currentChoice.value || ''}
                            onChange={(e) => handleInteractionChange(e.target.value)}
                            className="trait-dropdown"
                        >
                            <option value="" disabled>Select Cantrip...</option>
                            {wizardCantrips.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>
                )}

                {isInteractiveFightingStyle && (
                    <div className="feature-interaction">
                        <select
                            value={currentChoice.value || ''}
                            onChange={(e) => handleInteractionChange(e.target.value)}
                            className="trait-dropdown"
                        >
                            <option value="" disabled>Select Fighting Style...</option>
                            {FIGHTING_STYLE_OPTIONS.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>
                )}

                {isInteractiveMetamagic && (
                    <div className="feature-interaction">
                        <select
                            value={currentChoice.value || ''}
                            onChange={(e) => handleInteractionChange(e.target.value)}
                            className="trait-dropdown"
                        >
                            <option value="" disabled>Select Metamagic...</option>
                            {METAMAGIC_OPTIONS.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>
                )}

                {isInteractiveOriginFeat && (
                    <div className="feature-interaction">
                        <select
                            value={currentChoice.value || ''}
                            onChange={(e) => handleInteractionChange(e.target.value)}
                            className="trait-dropdown"
                        >
                            <option value="" disabled>Select Origin Feat...</option>
                            {FEATS.filter(f => f.category === 'Origin').map(f => {
                                const isTaken = computedFeats.some(cf => cf.name === f.name) && currentChoice.value !== f.name;
                                return (
                                    <option key={f.name} value={f.name} disabled={isTaken}>
                                        {f.name} {isTaken ? '(Already Active)' : ''}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                )}

                {isInteractiveInvocation && (
                    <div className="feature-interaction">
                        <select
                            value={currentChoice.value || ''}
                            onChange={(e) => handleInteractionChange(e.target.value)}
                            className="trait-dropdown"
                        >
                            <option value="" disabled>Select Invocation...</option>
                            {INVOCATION_OPTIONS.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </div>
                )}

                {(feat.interactive === 'skill' || feat.interactive === 'tool' || feat.interactive === 'instrument') && (
                    <div className="feature-interaction multi-select-container">
                        {Array.from({ length: feat.count || 1 }).map((_, idx) => {
                            // Current value is an array, or empty
                            const currentValues = Array.isArray(currentChoice.value) ? currentChoice.value : (currentChoice.value ? [currentChoice.value] : []);
                            const val = currentValues[idx] || '';

                            const handleChange = (newValue) => {
                                const newValues = [...currentValues];
                                // Ensure array is big enough
                                while (newValues.length <= idx) newValues.push('');
                                newValues[idx] = newValue;
                                // Filter out empty strings if needed, or keep them to maintain slot index?
                                // Better to keep index stability.
                                handleInteractionChange(newValues);
                            };

                            let options = [];
                            if (feat.interactive === 'skill') {
                                options = feat.options || ['Acrobatics', 'Animal Handling', 'Arcana', 'Athletics', 'Deception', 'History', 'Insight', 'Intimidation', 'Investigation', 'Medicine', 'Nature', 'Perception', 'Performance', 'Persuasion', 'Religion', 'Sleight of Hand', 'Stealth', 'Survival'];
                            } else if (feat.interactive === 'tool') {
                                if (feat.name === 'Tool Proficiency' && race === 'Dwarf') {
                                    options = ['Smith\'s Tools', 'Brewer\'s Supplies', 'Mason\'s Tools'];
                                } else {
                                    options = feat.options || TOOL_OPTIONS;
                                }
                            } else if (feat.interactive === 'instrument') {
                                options = feat.options || INSTRUMENT_OPTIONS;
                            }

                            // Filter taken: Global (knownSkills/Tools) AND Local siblings (other slots in this feature)
                            const isOptionDisabled = (opt) => {
                                // 1. Global check (unless it's the current value of THIS slot)
                                if (opt === val) return false;

                                // 2. Local sibling check
                                if (currentValues.includes(opt)) return true;

                                // 3. Global Proficiencies check
                                const globalList = feat.interactive === 'skill' ? knownSkills : knownTools;
                                return isTaken(opt, globalList);
                            };

                            return (
                                <div key={idx} className="multi-select-slot">
                                    <select
                                        value={val}
                                        onChange={(e) => handleChange(e.target.value)}
                                        className="trait-dropdown"
                                    >
                                        <option value="" disabled>Select {feat.interactive.charAt(0).toUpperCase() + feat.interactive.slice(1)}...</option>
                                        {options.map(opt => (
                                            <option key={opt} value={opt} disabled={isOptionDisabled(opt)}>
                                                {opt} {isOptionDisabled(opt) ? (currentValues.includes(opt) ? '(Selected)' : '(Taken)') : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            );
                        })}
                    </div>
                )}

                {isInteractiveASI && (
                    <ASIWall
                        uniqueKey={uniqueKey}
                        currentChoice={currentChoice}
                        updateFeatureChoice={updateFeatureChoice}
                        feats={FEATS}
                        computedFeats={computedFeats}
                    />
                )}
            </div>
        );
    };

    return (
        <div className="features-container">
            {raceFeatures.length > 0 && (
                <div className="features-split-section">
                    <h2 className="section-title">{race} Traits</h2>
                    <div className="features-grid">
                        {raceFeatures.map(renderFeature)}
                        {lineageFeatures.map(renderFeature)}
                    </div>
                </div>
            )}

            {background && bgFeatName && (
                <div className="features-split-section">
                    <h2 className="section-title">{background} Traits</h2>
                    <div className="features-grid">
                        <div className="feature-card">
                            <div className="feature-header">
                                <h3>{bgFeatName}</h3>
                                <span className="feature-level-badge">Background</span>
                            </div>
                            <div className="feature-meta">
                                <span className="feature-type">Origin Feat</span>
                            </div>
                            <p className="feature-desc">Granted by your background.</p>
                        </div>
                    </div>
                </div>
            )}

            <div className="features-split-section">
                <h2 className="section-title">{charClass} Traits</h2>
                <div className="features-grid">
                    {classFeatures.length === 0 ? <p>No traits unlocked.</p> : classFeatures.map(renderFeature)}
                </div>
            </div>

            {subclass && (
                <div className="features-split-section">
                    <h2 className="section-title subclass-title">{subclass} Traits</h2>
                    <div className="features-grid">
                        {subclassFeatures.length === 0 ? <p>No subclass traits unlocked.</p> : subclassFeatures.map(renderFeature)}
                    </div>
                </div>
            )}

            {genericFeatures.length > 0 && (
                <div className="features-split-section">
                    <h2 className="section-title">Other Traits</h2>
                    <div className="features-grid">
                        {genericFeatures.map(renderFeature)}
                    </div>
                </div>
            )}
        </div>
    );
};

// Sub-component for ASI logic to keep main clean
const ASIWall = ({ uniqueKey, currentChoice, updateFeatureChoice, feats, computedFeats }) => {
    const handleTypeChange = (type) => {
        // Reset value when switching type
        updateFeatureChoice(uniqueKey, type, type === 'asi' ? { strength: 2 } : '');
    };

    const level = parseInt(uniqueKey.split('-')[0]);

    // Filter feats based on category and level rules
    const availableFeats = feats.filter(f => {
        if (f.category === 'Origin') return false; // Origin feats are creation only

        // Epic Boon logic: Only available at Level 19+.
        // User Request: "also on level 19 i can pick any feat not only the boons appear on the list"
        // This implies they want to RESTRICT selection to Epic Boons at Level 19.
        if (level >= 19) {
            return f.category === 'Epic Boon';
        }

        // Below level 19, hide Epic Boons
        if (f.category === 'Epic Boon') return false;

        if (f.category === 'Fighting Style') return false; // Usually Class Feature specific, not ASI

        // Exclude if already active (unless it's the one currently selected in THIS slot)
        const isSelectedHere = currentChoice.type === 'feat' && currentChoice.value === f.name;
        // Check if active globally
        if (!isSelectedHere && computedFeats && computedFeats.some(af => af.name === f.name)) return false;

        return true;
    });

    return (
        <div className="feature-interaction asi-container">
            <div className="asi-type-selector">
                <button
                    className={`asi-btn ${currentChoice.type === 'asi' ? 'active' : ''}`}
                    onClick={() => handleTypeChange('asi')}
                >
                    Ability Score
                </button>
                <button
                    className={`asi-btn ${currentChoice.type === 'feat' ? 'active' : ''}`}
                    onClick={() => handleTypeChange('feat')}
                >
                    Feat
                </button>
            </div>

            {currentChoice.type === 'asi' && (
                <ASISelector
                    value={currentChoice.value}
                    onChange={(val) => updateFeatureChoice(uniqueKey, 'asi', val)}
                />
            )}

            {currentChoice.type === 'feat' && (
                <div className="asi-content">
                    <select
                        value={typeof currentChoice.value === 'string' ? currentChoice.value : ''}
                        onChange={(e) => updateFeatureChoice(uniqueKey, 'feat', e.target.value)}
                        className="trait-dropdown"
                    >
                        <option value="" disabled>Select a Feat...</option>
                        {availableFeats.map((f, i) => (
                            <option key={i} value={f.name}>{f.name}</option>
                        ))}
                    </select>
                </div>
            )}
        </div>
    );
};

const ASISelector = ({ value, onChange }) => {
    // Value format: { strength: 2 } OR { strength: 1, dexterity: 1 }
    // We need to decode this into [stat1, stat2] for the UI

    // Helper to decode
    const getSlots = () => {
        if (!value) return ['strength', 'strength'];
        const keys = Object.keys(value);
        if (keys.length === 1) return [keys[0], keys[0]]; // {str: 2} -> [str, str]
        if (keys.length === 2) return [keys[0], keys[1]]; // {str: 1, dex: 1} -> [str, dex]
        return ['strength', 'strength'];
    };

    const [slot1, slot2] = getSlots();

    const handleChange = (idx, newStat) => {
        const s1 = idx === 0 ? newStat : slot1;
        const s2 = idx === 1 ? newStat : slot2;

        // Construct new object
        const newValue = {};
        if (s1 === s2) {
            newValue[s1] = 2;
        } else {
            newValue[s1] = 1;
            newValue[s2] = 1;
        }
        onChange(newValue);
    };

    const abilities = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'];

    return (
        <div className="asi-content asi-slots">
            <div className="asi-slot">
                <label>+1 to</label>
                <select value={slot1} onChange={(e) => handleChange(0, e.target.value)} className="trait-dropdown">
                    {abilities.map(a => <option key={a} value={a}>{a.charAt(0).toUpperCase() + a.slice(1)}</option>)}
                </select>
            </div>
            <div className="asi-slot">
                <label>+1 to</label>
                <select value={slot2} onChange={(e) => handleChange(1, e.target.value)} className="trait-dropdown">
                    {abilities.map(a => <option key={a} value={a}>{a.charAt(0).toUpperCase() + a.slice(1)}</option>)}
                </select>
            </div>
        </div>
    );
};

export default Traits;
